import type { ReactNode } from 'react';
import type { TSparseItem } from '@modules/items/types/folders.types';
import type { TBaseStats } from '@shared/types/stats.types';
import type { TItem, TItemType, TResolvedItemsMap } from '@modules/items/types/items.types';

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/projects/tasks/types/tasks.types.ts
export enum ETaskStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
}

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/projects/tasks/types/tasks.types.ts
export enum ETaskPriority {
  NONE = 'NONE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/projects/tasks/types/tasks.types.ts
export enum ETaskDueState {
  OVERDUE = 'OVERDUE',
  DUE_SOON = 'DUE_SOON',
}
export type TTaskDue = { state: ETaskDueState; days: number };

export type TTaskPermissions = { canEdit: boolean; canArchive: boolean };

export type TTaskLinkedItemType = Extract<TItemType, 'FOLDER' | 'DOCUMENT' | 'FILE'>;
export type TTaskLinkedItem = { item: string; itemType: TTaskLinkedItemType };

export type TTask = {
  _id: string;
  organization: string;
  project: string;
  title: string;
  status: ETaskStatus;
  priority: ETaskPriority;
  linkedItems: TTaskLinkedItem[];
  archived: boolean;
  active: boolean;
  stats: TBaseStats;
  description?: string;
  assignee?: string;
  /** Calendar date serialized at UTC midnight. */
  dueDate?: string;
};
export type TTaskListItem = Pick<
  TTask,
  '_id' | 'title' | 'status' | 'priority' | 'linkedItems' | 'assignee' | 'dueDate'
> & {
  permissions: TTaskPermissions;
  due?: TTaskDue;
};

export type TTaskList = {
  items: TTaskListItem[];
  taskOrderRevision: number;
  permissions: { canCreate: boolean; canReorder: boolean };
  resolvedItems: TResolvedItemsMap;
};

export type TTaskTableSection = {
  id: string;
  label: string;
  header: ReactNode;
  tasks: TTaskListItem[];
  defaultExpanded: boolean;
};

export type TTaskListParams = {
  project: string;
  search: string;
  status: ETaskStatus[];
  assignee: string[];
};

export type TTaskDetails = {
  task: TTask;
  permissions: TTaskPermissions;
  resolvedItems: TResolvedItemsMap;
  due?: TTaskDue;
};

export type TTaskValues = Pick<TTask, 'title' | 'priority' | 'linkedItems'> & {
  description: string | null;
  assignee: string | null;
  dueDate: string | null;
};
export type TTaskFormValues = Omit<TTaskValues, 'priority' | 'description' | 'linkedItems'> & {
  description: string;
  priority: ETaskPriority[];
  linkedItems: TItem[];
};
// Linked items change by adds and removals, so a save never undoes links that someone else changed meanwhile.
export type TUpdateTaskDetails = Partial<Omit<TTaskValues, 'linkedItems'>> & {
  addedLinkedItems?: TTaskLinkedItem[];
  removedLinkedItems?: TTaskLinkedItem[];
};

export type TReorderTasks = {
  project: string;
  taskId: string;
  previousTaskId: string | null;
  nextTaskId: string | null;
  expectedRevision: number;
};

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/projects/tasks/types/tasks.types.ts
export enum ETaskHistoryType {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  ARCHIVED = 'ARCHIVED',
}

export type TTaskChange<T> = { before: T; after: T };
export type TTaskActivityChanges = {
  title?: TTaskChange<string>;
  status?: TTaskChange<ETaskStatus>;
  priority?: TTaskChange<ETaskPriority>;
  linkedItems?: { added: TTaskLinkedItem[]; removed: TTaskLinkedItem[] };
  description?: Partial<TTaskChange<string>>;
  assignee?: Partial<TTaskChange<string>>;
  dueDate?: Partial<TTaskChange<string>>;
};
type TTaskActivityBase = {
  task: TSparseItem & Pick<TTask, 'archived'>;
  createdDate: string;
  createdByUser: string;
};
export type TTaskActivity =
  | ({ type: ETaskHistoryType.CREATED | ETaskHistoryType.ARCHIVED } & TTaskActivityBase)
  | ({ type: ETaskHistoryType.UPDATED } & TTaskActivityBase & { changes: TTaskActivityChanges });

export type TTaskDashboard = {
  statusCounts: Record<ETaskStatus, number>;
  needsAttention: {
    overdue: TTaskListItem[];
    dueSoon: TTaskListItem[];
    upcoming: TTaskListItem[];
    unassigned: TTaskListItem[];
  };
  activities: TTaskActivity[];
};
